import React from 'react';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  INVOICE_PAYMENT_STATUS_LABELS,
  INVOICE_STATUS_LABELS,
  InvoiceBatchStatusEnum,
  InvoicePaymentStatusEnum,
  InvoiceRecipientStatusEnum,
  InvoiceStateEnum,
  InvoiceStatusEnum,
  RECIPIENT_STATUS_LABELS
} from '../common/enums/invoicing.enum';

export type StatusTone = 'default' | 'primary' | 'success' | 'warning' | 'error';

type StatusMap<T extends string> = Record<T, { tone: StatusTone; label: string }>;

/**
 * Every status the admin shows as a chip, with its colour and its label, in one place.
 *
 * Three screens used to keep their own copy of this, and they disagreed: the invoice page
 * painted a draft green, the list painted it orange. Warning is "still to do", success is
 * "done", error is "undone" - the same word means the same colour wherever it appears.
 */
export const STATUS_CHIPS: {
  invoice: StatusMap<InvoiceStatusEnum>;
  payment: StatusMap<InvoicePaymentStatusEnum>;
  batch: StatusMap<InvoiceBatchStatusEnum>;
  invoiceState: StatusMap<InvoiceStateEnum>;
  recipient: StatusMap<InvoiceRecipientStatusEnum>;
} = {
  // the list's one state: issued-and-waiting is primary (nothing wrong, nothing done
  // yet), money owed in part is still to do, money in full is done
  invoiceState: {
    [InvoiceStateEnum.DRAFT]: { tone: 'warning', label: 'enums.invoiceState.DRAFT' },
    [InvoiceStateEnum.ISSUED_NOT_SENT]: { tone: 'primary', label: 'enums.invoiceState.ISSUED_NOT_SENT' },
    [InvoiceStateEnum.SENT]: { tone: 'primary', label: 'enums.invoiceState.SENT' },
    [InvoiceStateEnum.SEND_FAILED]: { tone: 'error', label: 'enums.invoiceState.SEND_FAILED' },
    [InvoiceStateEnum.PARTIALLY_PAID]: { tone: 'warning', label: 'enums.invoiceState.PARTIALLY_PAID' },
    [InvoiceStateEnum.PAID]: { tone: 'success', label: 'enums.invoiceState.PAID' },
    [InvoiceStateEnum.CANCELLED]: { tone: 'error', label: 'enums.invoiceState.CANCELLED' }
  },
  invoice: {
    [InvoiceStatusEnum.DRAFT]: { tone: 'warning', label: INVOICE_STATUS_LABELS[InvoiceStatusEnum.DRAFT] },
    [InvoiceStatusEnum.ISSUED]: { tone: 'success', label: INVOICE_STATUS_LABELS[InvoiceStatusEnum.ISSUED] },
    [InvoiceStatusEnum.CANCELLED]: { tone: 'error', label: INVOICE_STATUS_LABELS[InvoiceStatusEnum.CANCELLED] }
  },
  payment: {
    [InvoicePaymentStatusEnum.UNPAID]: { tone: 'default', label: INVOICE_PAYMENT_STATUS_LABELS[InvoicePaymentStatusEnum.UNPAID] },
    [InvoicePaymentStatusEnum.PARTIALLY_PAID]: {
      tone: 'warning',
      label: INVOICE_PAYMENT_STATUS_LABELS[InvoicePaymentStatusEnum.PARTIALLY_PAID]
    },
    [InvoicePaymentStatusEnum.PAID]: { tone: 'success', label: INVOICE_PAYMENT_STATUS_LABELS[InvoicePaymentStatusEnum.PAID] }
  },
  batch: {
    [InvoiceBatchStatusEnum.DRAFTS_READY]: { tone: 'warning', label: 'enums.batchStatus.DRAFTS_READY' },
    [InvoiceBatchStatusEnum.ISSUED]: { tone: 'success', label: 'enums.batchStatus.ISSUED' }
  },
  // one address of an invoice's delivery; withdrawn is neither done nor wrong, just dropped
  recipient: {
    [InvoiceRecipientStatusEnum.QUEUED]: { tone: 'primary', label: RECIPIENT_STATUS_LABELS[InvoiceRecipientStatusEnum.QUEUED] },
    [InvoiceRecipientStatusEnum.SENT]: { tone: 'success', label: RECIPIENT_STATUS_LABELS[InvoiceRecipientStatusEnum.SENT] },
    [InvoiceRecipientStatusEnum.FAILED]: { tone: 'error', label: RECIPIENT_STATUS_LABELS[InvoiceRecipientStatusEnum.FAILED] },
    [InvoiceRecipientStatusEnum.WITHDRAWN]: { tone: 'default', label: RECIPIENT_STATUS_LABELS[InvoiceRecipientStatusEnum.WITHDRAWN] }
  }
};

type StatusChipProps =
  | { kind: 'invoice'; status: InvoiceStatusEnum }
  | { kind: 'payment'; status: InvoicePaymentStatusEnum }
  | { kind: 'batch'; status: InvoiceBatchStatusEnum }
  | { kind: 'invoiceState'; status: InvoiceStateEnum }
  | { kind: 'recipient'; status: InvoiceRecipientStatusEnum };

const StatusChip: React.FC<StatusChipProps> = ({ kind, status }) => {
  const { t } = useTranslation();
  const entry = (STATUS_CHIPS[kind] as StatusMap<string>)[status];
  if (!entry) {
    // a status this build does not know - a newer API, or a missing field - is shown as
    // it came rather than taking the whole page down with it
    // eslint-disable-next-line no-console
    console.warn(`StatusChip: unknown ${kind} status`, status);
    return <Chip size="small" variant="outlined" label={status ?? '—'} data-tone="default" data-testid="status-chip" />;
  }
  const { tone, label } = entry;
  return <Chip size="small" variant="outlined" color={tone} label={t(label)} data-tone={tone} data-testid="status-chip" />;
};

export default StatusChip;
